<template>
  <div class="crud-patrocinador">
    <!-- ── HEADER ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title-wrap">Patrocinadores</h1>
        <p class="page-sub">
          Evento activo · {{ patrocinadoresFiltrados.length }} registros
        </p>
      </div>
      <div class="header-actions">
        <div class="busqueda-wrap">
          <i class="bi bi-search busqueda-icon"></i>
          <input
            v-model="busqueda"
            class="input-busqueda"
            placeholder="Buscar por marca o email..."
          />
          <button v-if="busqueda" class="busqueda-clear" @click="busqueda = ''">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <button class="btn-primary" @click="abrirCrear">
          <i class="bi bi-plus-lg"></i> Agregar patrocinador
        </button>
      </div>
    </div>

    <!-- ── TABLA ── -->
    <div class="tabla-wrapper">
      <div v-if="cargando" class="estado-centro">
        <span class="spinner-border spinner-border-sm text-warning me-2"></span>
        Cargando patrocinadores...
      </div>

      <div v-else-if="!patrocinadoresFiltrados.length" class="estado-centro vacio">
        <i class="bi bi-building-slash" style="font-size: 2rem; opacity: 0.3"></i>
        <p>Sin patrocinadores registrados</p>
      </div>

      <table v-else class="tabla">
        <thead>
          <tr>
            <th>Marca</th>
            <th>Email</th>
            <th>Redes</th>
            <th>Confirmado</th>
            <th>Activo</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in patrocinadoresFiltrados" :key="p.id" class="tabla-fila">
            <td>
              <div class="patrocinador-cell">
                <img
                  v-if="p.logo"
                  :src="p.logo"
                  class="logo-thumb"
                  :alt="p.nombre_marca"
                />
                <div v-else class="avatar-placeholder">
                  {{ p.nombre_marca?.charAt(0) }}
                </div>
                <p class="nombre">{{ p.nombre_marca }}</p>
              </div>
            </td>
            <td>{{ p.email }}</td>
            <td>
              <div class="redes-cell">
                <a
                  v-if="p.redes_sociales?.sitio_web"
                  :href="p.redes_sociales.sitio_web"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Sitio web"
                >
                  <i class="bi bi-globe2"></i>
                </a>
                <a
                  v-if="p.redes_sociales?.instagram"
                  :href="p.redes_sociales.instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Instagram"
                >
                  <i class="bi bi-instagram"></i>
                </a>
                <a
                  v-if="p.redes_sociales?.facebook"
                  :href="p.redes_sociales.facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Facebook"
                >
                  <i class="bi bi-facebook"></i>
                </a>
                <a
                  v-if="p.redes_sociales?.tiktok"
                  :href="p.redes_sociales.tiktok"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="TikTok"
                >
                  <i class="bi bi-tiktok"></i>
                </a>
                <a
                  v-if="p.redes_sociales?.youtube"
                  :href="p.redes_sociales.youtube"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="YouTube"
                >
                  <i class="bi bi-youtube"></i>
                </a>
                <span v-if="!tieneRedes(p)" class="sin-redes">—</span>
              </div>
            </td>
            <td>
              <button
                class="badge-toggle"
                :class="p.confirmado ? 'badge-confirmado' : 'badge-pendiente'"
                :disabled="actualizandoId === p.id"
                @click="toggleConfirmado(p)"
              >
                {{ p.confirmado ? "Confirmado" : "Pendiente" }}
              </button>
            </td>
            <td>
              <button
                class="badge-toggle"
                :class="p.activo ? 'badge-activo' : 'badge-inactivo'"
                :disabled="actualizandoId === p.id"
                @click="toggleActivo(p)"
              >
                {{ p.activo ? "Activo" : "Inactivo" }}
              </button>
            </td>
            <td>
              <button class="btn-icon" title="Editar" @click="abrirEditar(p)">
                <i class="bi bi-pencil-fill"></i>
              </button>
              <button
                class="btn-icon btn-danger-ghost"
                title="Eliminar"
                @click="confirmarEliminar(p)"
              >
                <i class="bi bi-trash3"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════
         MODAL — CREAR / EDITAR PATROCINADOR
    ════════════════════════════════════════════════════════════════════ -->
    <div v-if="modalAbierto" class="modal-overlay" @click.self="cerrarModal">
      <div class="modal-panel modal-mediano">
        <div class="modal-header">
          <div>
            <h2 class="modal-titulo">
              {{ modoEdicion ? "Editar patrocinador" : "Nuevo patrocinador" }}
            </h2>
            <p class="modal-sub">
              {{
                modoEdicion
                  ? patrocinadorForm.nombre_marca
                  : "Completa los datos de la marca."
              }}
            </p>
          </div>
          <button class="btn-icon" @click="cerrarModal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="form-grid">
          <!-- ── Logo ── -->
          <div class="form-group col-span-2">
            <label><i class="bi bi-image-fill"></i> Logo *</label>
            <div
              class="foto-upload-zone"
              :class="{ 'foto-drag-over': logoDragOver }"
              @click="inputLogo?.click()"
              @dragover.prevent="logoDragOver = true"
              @dragleave.prevent="logoDragOver = false"
              @drop.prevent="
                (e) => {
                  logoDragOver = false;
                  onLogoDrop(e);
                }
              "
            >
              <img
                v-if="logoPreview || patrocinadorForm.logo"
                :src="logoPreview || patrocinadorForm.logo"
                class="foto-preview"
                alt="Logo"
              />
              <div v-else class="foto-placeholder">
                <i class="bi bi-image"></i>
                <span>Arrastra el logo o haz clic para seleccionar</span>
                <span class="foto-hint">JPG, PNG o WEBP</span>
              </div>
              <div v-if="logoPreview || patrocinadorForm.logo" class="foto-hover-overlay">
                <i class="bi bi-camera-fill"></i>
                <span>Cambiar logo</span>
              </div>
            </div>
            <div v-if="logoNuevo" class="foto-nueva-badge">
              <i class="bi bi-check-circle-fill text-success"></i>
              {{ logoNuevo.name }}
              <button
                class="btn-quitar-foto"
                @click.stop="quitarLogoNuevo"
                title="Quitar"
              >
                <i class="bi bi-x"></i>
              </button>
            </div>
            <input
              ref="inputLogo"
              type="file"
              accept="image/*"
              style="display: none"
              @change="onLogoSeleccionado"
            />
          </div>

          <div class="form-group">
            <label>Nombre de la marca *</label>
            <input
              v-model="patrocinadorForm.nombre_marca"
              class="input-form"
              placeholder="Ej. ArmBar Brand"
            />
          </div>
          <div class="form-group">
            <label>Email *</label>
            <input
              v-model="patrocinadorForm.email"
              type="email"
              class="input-form"
              placeholder="contacto@marca.com"
            />
          </div>

          <div class="form-section-label col-span-2">
            <i class="bi bi-share-fill"></i> Redes sociales
          </div>
          <div class="form-group">
            <label><i class="bi bi-globe2"></i> Sitio web</label>
            <input
              v-model="patrocinadorForm.redes_sociales.sitio_web"
              type="url"
              class="input-form"
              placeholder="https://marca.com"
            />
          </div>
          <div class="form-group">
            <label><i class="bi bi-instagram"></i> Instagram</label>
            <input
              v-model="patrocinadorForm.redes_sociales.instagram"
              type="url"
              class="input-form"
              placeholder="https://instagram.com/..."
            />
          </div>
          <div class="form-group">
            <label><i class="bi bi-facebook"></i> Facebook</label>
            <input
              v-model="patrocinadorForm.redes_sociales.facebook"
              type="url"
              class="input-form"
              placeholder="https://facebook.com/..."
            />
          </div>
          <div class="form-group">
            <label><i class="bi bi-tiktok"></i> TikTok</label>
            <input
              v-model="patrocinadorForm.redes_sociales.tiktok"
              type="url"
              class="input-form"
              placeholder="https://tiktok.com/@..."
            />
          </div>
          <div class="form-group">
            <label><i class="bi bi-youtube"></i> YouTube</label>
            <input
              v-model="patrocinadorForm.redes_sociales.youtube"
              type="url"
              class="input-form"
              placeholder="https://youtube.com/..."
            />
          </div>

          <div class="form-section-label col-span-2">
            <i class="bi bi-toggles"></i> Estado
          </div>
          <div class="form-group">
            <label class="check-label">
              <input
                type="checkbox"
                v-model="patrocinadorForm.confirmado"
                class="check-notif"
              />
              Confirmado
            </label>
          </div>
          <div class="form-group">
            <label class="check-label">
              <input
                type="checkbox"
                v-model="patrocinadorForm.activo"
                class="check-notif"
              />
              Activo (visible en landing)
            </label>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="cerrarModal">Cancelar</button>
          <button class="btn-primary" :disabled="guardando" @click="guardar">
            <span v-if="guardando">
              <span class="spinner-border spinner-border-sm"></span>
            </span>
            <span v-else><i class="bi bi-floppy-fill"></i> Guardar</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import Swal from "sweetalert2";
import {
  getPatrocinadoresDashboard,
  crearPatrocinadorDashboard,
  editarPatrocinadorDashboard,
  eliminarPatrocinadorDashboard,
} from "@/utils/api_dashboard";

// ── Estado principal ─────────────────────────────────────────────────────────
const patrocinadores = ref([]);
const cargando = ref(false);
const busqueda = ref("");
const actualizandoId = ref(null);

const patrocinadoresFiltrados = computed(() => {
  const q = busqueda.value.trim().toLowerCase();
  if (!q) return patrocinadores.value;
  return patrocinadores.value.filter(
    (p) =>
      (p.nombre_marca ?? "").toLowerCase().includes(q) ||
      (p.email ?? "").toLowerCase().includes(q)
  );
});

function tieneRedes(p) {
  const r = p.redes_sociales || {};
  return !!(r.sitio_web || r.instagram || r.facebook || r.tiktok || r.youtube);
}

// ── Carga ────────────────────────────────────────────────────────────────────
async function cargar() {
  cargando.value = true;
  try {
    patrocinadores.value = await getPatrocinadoresDashboard();
  } catch {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo cargar la lista de patrocinadores.",
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
  } finally {
    cargando.value = false;
  }
}
onMounted(cargar);

// ── Toggles rápidos (confirmado / activo) ────────────────────────────────────
async function toggleConfirmado(p) {
  actualizandoId.value = p.id;
  const anterior = p.confirmado;
  p.confirmado = !p.confirmado;
  try {
    await editarPatrocinadorDashboard(p.id, { confirmado: p.confirmado });
  } catch {
    p.confirmado = anterior;
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo actualizar.",
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
  } finally {
    actualizandoId.value = null;
  }
}

async function toggleActivo(p) {
  actualizandoId.value = p.id;
  const anterior = p.activo;
  p.activo = !p.activo;
  try {
    await editarPatrocinadorDashboard(p.id, { activo: p.activo });
  } catch {
    p.activo = anterior;
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo actualizar.",
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
  } finally {
    actualizandoId.value = null;
  }
}

// ── Modal crear / editar ──────────────────────────────────────────────────────
const modalAbierto = ref(false);
const modoEdicion = ref(false);
const guardando = ref(false);
const patrocinadorForm = ref(formVacio());

function formVacio() {
  return {
    id: null,
    nombre_marca: "",
    email: "",
    logo: null,
    confirmado: false,
    activo: true,
    redes_sociales: {
      sitio_web: "",
      instagram: "",
      facebook: "",
      tiktok: "",
      youtube: "",
    },
  };
}

function abrirCrear() {
  modoEdicion.value = false;
  patrocinadorForm.value = formVacio();
  logoNuevo.value = null;
  logoPreview.value = null;
  modalAbierto.value = true;
}

function abrirEditar(p) {
  modoEdicion.value = true;
  patrocinadorForm.value = {
    ...p,
    redes_sociales: {
      sitio_web: "",
      instagram: "",
      facebook: "",
      tiktok: "",
      youtube: "",
      ...(p.redes_sociales || {}),
    },
  };
  logoNuevo.value = null;
  logoPreview.value = null;
  modalAbierto.value = true;
}

function cerrarModal() {
  modalAbierto.value = false;
  patrocinadorForm.value = formVacio();
  logoNuevo.value = null;
  logoPreview.value = null;
}

// ── Manejo de logo ────────────────────────────────────────────────────────────
const logoNuevo = ref(null);
const logoPreview = ref(null);
const inputLogo = ref(null);
const logoDragOver = ref(false);

function onLogoSeleccionado(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  if (!file.type.startsWith("image/")) {
    Swal.fire({
      icon: "error",
      title: "Archivo inválido",
      text: "Solo se permiten imágenes (JPG, PNG, WEBP).",
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
    return;
  }
  logoNuevo.value = file;
  const reader = new FileReader();
  reader.onload = (ev) => {
    logoPreview.value = ev.target.result;
  };
  reader.readAsDataURL(file);
}

function onLogoDrop(e) {
  const file = e.dataTransfer.files?.[0];
  if (!file) return;
  onLogoSeleccionado({ target: { files: [file] } });
}

function quitarLogoNuevo() {
  logoNuevo.value = null;
  logoPreview.value = null;
  if (inputLogo.value) inputLogo.value.value = "";
}

// ── Guardar (crear o editar) ──────────────────────────────────────────────────
function limpiarRedes(redes) {
  const limpio = {};
  for (const [k, v] of Object.entries(redes || {})) {
    if (v && v.trim()) limpio[k] = v.trim();
  }
  return limpio;
}

async function guardar() {
  const p = patrocinadorForm.value;

  if (!p.nombre_marca?.trim()) {
    Swal.fire({
      icon: "warning",
      title: "Falta el nombre de la marca",
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
    return;
  }
  if (!p.email?.trim()) {
    Swal.fire({
      icon: "warning",
      title: "Falta el email",
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
    return;
  }
  if (!modoEdicion.value && !logoNuevo.value) {
    Swal.fire({
      icon: "warning",
      title: "Falta el logo",
      text: "El logo es obligatorio para dar de alta un patrocinador.",
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
    return;
  }

  guardando.value = true;
  try {
    if (logoNuevo.value) {
      // Hay logo nuevo (alta, o edición cambiando el logo) → multipart/form-data
      const fd = new FormData();
      fd.append("nombre_marca", p.nombre_marca);
      fd.append("email", p.email);
      fd.append("confirmado", p.confirmado);
      fd.append("activo", p.activo);
      fd.append("redes_sociales", JSON.stringify(limpiarRedes(p.redes_sociales)));
      fd.append("logo", logoNuevo.value);

      if (modoEdicion.value) {
        await editarPatrocinadorDashboard(p.id, fd);
      } else {
        await crearPatrocinadorDashboard(fd);
      }
    } else {
      // Edición sin tocar el logo → JSON normal
      await editarPatrocinadorDashboard(p.id, {
        nombre_marca: p.nombre_marca,
        email: p.email,
        confirmado: p.confirmado,
        activo: p.activo,
        redes_sociales: limpiarRedes(p.redes_sociales),
      });
    }

    Swal.fire({
      icon: "success",
      title: "Guardado",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 2000,
      background: "#0f1520",
      color: "#fff",
    });
    cerrarModal();
    cargar();
  } catch (e) {
    const msg = e.response?.data ? JSON.stringify(e.response.data) : "Error al guardar.";
    Swal.fire({
      icon: "error",
      title: "Error",
      text: msg,
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
  } finally {
    guardando.value = false;
  }
}

// ── Eliminar ──────────────────────────────────────────────────────────────────
async function confirmarEliminar(p) {
  const resultado = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar patrocinador?",
    text: `Se eliminará "${p.nombre_marca}" permanentemente.`,
    showCancelButton: true,
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "rgba(255,255,255,0.2)",
    background: "#0f1520",
    color: "#fff",
  });
  if (!resultado.isConfirmed) return;

  try {
    await eliminarPatrocinadorDashboard(p.id);
    patrocinadores.value = patrocinadores.value.filter((x) => x.id !== p.id);
    Swal.fire({
      icon: "success",
      title: "Eliminado",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 2000,
      background: "#0f1520",
      color: "#fff",
    });
  } catch {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo eliminar.",
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
  }
}
</script>

<style scoped>
.crud-patrocinador {
  padding: 32px;
  font-family: "Inter", sans-serif;
  color: #fff;
  min-height: 100%;
}

/* ── Header ── */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}
.page-title-wrap {
  color: #f3bc2c;
  font-size: 22px;
  font-weight: 700;
  margin: 0;
}
.page-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  margin: 4px 0 0;
}
.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

/* ── Búsqueda ── */
.busqueda-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.busqueda-icon {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
  pointer-events: none;
}
.input-busqueda {
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  padding: 7px 32px 7px 30px;
  font-size: 13px;
  width: 260px;
  outline: none;
  transition: border-color 0.15s, width 0.2s;
  font-family: "Inter", sans-serif;
}
.input-busqueda::placeholder {
  color: rgba(255, 255, 255, 0.25);
}
.input-busqueda:focus {
  border-color: rgba(243, 188, 44, 0.5);
  width: 300px;
}
.busqueda-clear {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  padding: 0;
  font-size: 15px;
  line-height: 1;
  transition: color 0.15s;
}
.busqueda-clear:hover {
  color: #f87171;
}

/* ── Tabla ── */
.tabla-wrapper {
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}
.estado-centro {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 10px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 14px;
}
.tabla {
  width: 100%;
  min-width: 700px;
  border-collapse: collapse;
  font-size: 13px;
}
.tabla thead tr {
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.tabla th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
  white-space: nowrap;
}
.tabla-fila {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.15s;
}
.tabla-fila:hover {
  background: rgba(255, 255, 255, 0.03);
}
.tabla td {
  padding: 12px 16px;
  vertical-align: middle;
}

.patrocinador-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.logo-thumb {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  object-fit: contain;
  background: #fff;
  padding: 2px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.avatar-placeholder {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(243, 188, 44, 0.2);
  color: #f3bc2c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}
.nombre {
  font-weight: 600;
  margin: 0;
  font-size: 13px;
}

.redes-cell {
  display: flex;
  gap: 10px;
  align-items: center;
}
.redes-cell a {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  transition: color 0.15s;
}
.redes-cell a:hover {
  color: #f3bc2c;
}
.sin-redes {
  color: rgba(255, 255, 255, 0.25);
  font-size: 12px;
}

/* ── Badges toggle (confirmado / activo) ── */
.badge-toggle {
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s;
}
.badge-toggle:hover:not(:disabled) {
  opacity: 0.8;
}
.badge-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.badge-confirmado {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}
.badge-pendiente {
  background: rgba(234, 179, 8, 0.15);
  color: #facc15;
}
.badge-activo {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}
.badge-inactivo {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.5);
}

/* ── Botones ── */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #f3bc2c;
  color: #0f172a;
  border: none;
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}
.btn-primary:hover:not(:disabled) {
  background: #e5ae24;
}
.btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-secondary:hover {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.05);
}
.btn-icon {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.15s;
}
.btn-icon:hover {
  color: #f3bc2c;
  background: rgba(243, 188, 44, 0.1);
}
.btn-danger-ghost:hover {
  color: #f87171;
  background: rgba(239, 68, 68, 0.1);
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.modal-panel {
  background: #0f1520;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  width: 100%;
}
.modal-mediano {
  max-width: 600px;
}
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
.modal-titulo {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  color: #f3bc2c;
}
.modal-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin: 4px 0 0;
}
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  flex-wrap: wrap;
}

/* ── Form grid ── */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.col-span-2 {
  grid-column: span 2;
}
label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
  display: flex;
  align-items: center;
  gap: 5px;
}
.check-label {
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  color: #fff;
  cursor: pointer;
}
.input-form {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #fff;
  padding: 9px 12px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
  width: 100%;
  font-family: "Inter", sans-serif;
  box-sizing: border-box;
}
.input-form:focus {
  border-color: rgba(243, 188, 44, 0.5);
}
.form-section-label {
  grid-column: span 2;
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #f3bc2c;
  padding: 6px 0 2px;
  border-bottom: 1px solid rgba(243, 188, 44, 0.15);
}

/* ── Logo upload ── */
.foto-upload-zone {
  position: relative;
  width: 100%;
  height: 160px;
  border: 2px dashed rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s, background 0.2s;
  background: rgba(255, 255, 255, 0.03);
  display: flex;
  align-items: center;
  justify-content: center;
}
.foto-upload-zone:hover {
  border-color: rgba(243, 188, 44, 0.5);
  background: rgba(243, 188, 44, 0.04);
}
.foto-drag-over {
  border-color: #f3bc2c !important;
  background: rgba(243, 188, 44, 0.08) !important;
}
.foto-preview {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #fff;
}
.foto-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
  text-align: center;
  padding: 16px;
}
.foto-placeholder i {
  font-size: 32px;
  opacity: 0.4;
}
.foto-hint {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.2);
}
.foto-hover-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.2s;
}
.foto-hover-overlay i {
  font-size: 22px;
}
.foto-upload-zone:hover .foto-hover-overlay {
  opacity: 1;
}
.foto-nueva-badge {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(74, 222, 128, 0.08);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 6px;
  padding: 5px 10px;
}
.btn-quitar-foto {
  margin-left: auto;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  font-size: 15px;
  transition: color 0.15s;
}
.btn-quitar-foto:hover {
  color: #f87171;
}
.text-success {
  color: #4ade80;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .crud-patrocinador {
    padding: 16px;
  }
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }
  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }
  .busqueda-wrap {
    width: 100%;
  }
  .input-busqueda {
    width: 100%;
    box-sizing: border-box;
  }
  .header-actions .btn-primary {
    width: 100%;
    justify-content: center;
  }
  .modal-overlay {
    padding: 12px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .col-span-2 {
    grid-column: span 1;
  }
}
</style>
